import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Upload, Send, X, Paperclip, Calendar as CalendarIcon, Trash2, CalendarDays, BookmarkPlus, Pencil, MessageCircle } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { useLang } from "../context/LangContext";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../components/ui/sheet";
import { Badge } from "../components/ui/badge";
import { ASSIGNABLE_ROLES } from "../lib/permissions";

const STATUS_COLS = ["pending", "in_progress", "review", "completed"];
const STATUS_TINT = {
  pending: "border-amber-400/30 bg-amber-400/5",
  in_progress: "border-blue-400/30 bg-blue-400/5",
  review: "border-violet-400/30 bg-violet-400/5",
  completed: "border-emerald-400/30 bg-emerald-400/5",
  rejected: "border-rose-400/30 bg-rose-400/5",
};
const PRIORITY_TINT = {
  low: "text-slate-300 bg-white/5 border-white/10",
  medium: "text-blue-300 bg-blue-400/10 border-blue-400/20",
  high: "text-amber-300 bg-amber-400/10 border-amber-400/20",
  urgent: "text-rose-300 bg-rose-400/10 border-rose-400/20",
};

function useUsers() {
  const [users, setUsers] = useState([]);
  useEffect(() => {
    api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
  }, []);
  return users;
}

function WhatsAppNotify({ order, users, t }) {
  const assignee = users.find((u) => u.id === order.assigned_to);
  const digits = (assignee?.phone || "").replace(/\D/g, "");
  const disabled = !assignee || !digits;
  const open = () => {
    const msg = `[Cen21 Marketing] Bonjour ${assignee.name}, une commande vous est assignée : « ${order.title} » — échéance ${order.deadline ? order.deadline.slice(0, 10) : "non définie"}. Détails : ${window.location.origin}/orders`;
    window.open(`https://wa.me/${digits}?text=${encodeURIComponent(msg)}`, "_blank", "noopener");
  };
  return (
    <Button
      size="sm"
      variant="outline"
      data-testid="order-whatsapp-notify-btn"
      disabled={disabled}
      onClick={open}
      title={!order.assigned_to ? t("whatsapp_no_assignee") : disabled ? t("whatsapp_no_phone") : t("notify_whatsapp")}
      className="border-emerald-400/30 bg-emerald-400/10 text-emerald-300 hover:bg-emerald-400/20 disabled:opacity-40"
    >
      <MessageCircle className="w-3.5 h-3.5 mr-1.5" />
      {t("notify_whatsapp")}
    </Button>
  );
}

function FileList({ items, t }) {
  if (!items?.length) return null;
  return (
    <div className="mt-2 flex flex-wrap gap-2">
      {items.map((f) => (
        <a
          key={f.id || f.storage_path}
          href={`${api.defaults.baseURL}/files/${f.storage_path}?auth=${localStorage.getItem("access_token") || ""}`}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-lg bg-white/5 border border-white/10 hover:bg-white/10"
        >
          <Paperclip className="w-3 h-3" />
          {f.original_filename || "file"}
        </a>
      ))}
    </div>
  );
}

function UploadButton({ onUpload, label, testid }) {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const handle = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onUpload(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };
  return (
    <>
      <input ref={ref} type="file" onChange={handle} hidden />
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => ref.current.click()}
        disabled={busy}
        data-testid={testid}
        className="border-white/10 bg-white/5 hover:bg-white/10"
      >
        <Upload className="w-3.5 h-3.5 mr-1.5" />
        {busy ? "…" : label}
      </Button>
    </>
  );
}

export default function Orders() {
  const { t } = useLang();
  const { user } = useAuth();
  const users = useUsers();
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState({ status: "", type: "", assigned_to: "", event_id: "" });
  const [events, setEvents] = useState([]);
  useEffect(() => { api.get("/events").then((r) => setEvents(r.data)).catch(() => {}); }, []);
  const [openCreate, setOpenCreate] = useState(false);
  const [selected, setSelected] = useState(null);
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();

  const reqRef = useRef(0);
  const load = useCallback(async () => {
    const params = {};
    if (filter.status) params.status = filter.status;
    if (filter.type) params.type = filter.type;
    if (filter.assigned_to) params.assigned_to = filter.assigned_to;
    if (filter.event_id) params.event_id = filter.event_id;
    const seq = ++reqRef.current;
    const { data } = await api.get("/orders", { params });
    if (seq === reqRef.current) setOrders(data);
  }, [filter.status, filter.type, filter.assigned_to, filter.event_id]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const ev = params.get("event");
    if (ev) { setFilter((f) => ({ ...f, event_id: ev })); setParams({}, { replace: true }); }
  }, [params, setParams]);

  useEffect(() => {
    const id = params.get("open");
    if (id && orders.length) {
      const o = orders.find((x) => x.id === id);
      if (o) { setSelected(o); setParams({}, { replace: true }); }
    }
  }, [orders, params, setParams]);

  const byStatus = useMemo(() => {
    const map = Object.fromEntries(STATUS_COLS.map((s) => [s, []]));
    for (const o of orders) if (map[o.status]) map[o.status].push(o);
    return map;
  }, [orders]);

  const [dragId, setDragId] = useState(null);
  const [overCol, setOverCol] = useState(null);
  const dropTo = async (status) => {
    setOverCol(null);
    const o = orders.find((x) => x.id === dragId);
    setDragId(null);
    if (!o || o.status === status) return;
    setOrders((prev) => prev.map((x) => (x.id === o.id ? { ...x, status } : x)));
    try {
      await api.patch(`/orders/${o.id}`, { status });
      toast.success(`${o.title} → ${t(status)}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
    load();
  };

  return (
    <div className="space-y-6" data-testid="orders-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight" style={{ fontFamily: "Outfit" }}>{t("orders")}</h1>
          <p className="text-slate-400 mt-1 text-sm">Kanban · {orders.length} · {t("drag_hint")}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" onClick={() => nav("/calendar")} data-testid="orders-calendar-link" className="border-white/10 bg-white/5">
            <CalendarDays className="w-4 h-4 mr-1.5" />{t("calendar")}
          </Button>
          <Select value={filter.status || "all"} onValueChange={(v) => setFilter({ ...filter, status: v === "all" ? "" : v })}>
            <SelectTrigger data-testid="order-filter-status" className="w-[160px] bg-white/5 border-white/10">
              <SelectValue placeholder={t("filter_status")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("all")}</SelectItem>
              {STATUS_COLS.map((s) => <SelectItem key={s} value={s}>{t(s)}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filter.type || "all"} onValueChange={(v) => setFilter({ ...filter, type: v === "all" ? "" : v })}>
            <SelectTrigger data-testid="order-filter-type" className="w-[140px] bg-white/5 border-white/10">
              <SelectValue placeholder={t("filter_type")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("all")}</SelectItem>
              <SelectItem value="poster">{t("poster")}</SelectItem>
              <SelectItem value="video">{t("video")}</SelectItem>
              <SelectItem value="photo">{t("photo")}</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filter.assigned_to || "all"} onValueChange={(v) => setFilter({ ...filter, assigned_to: v === "all" ? "" : v })}>
            <SelectTrigger data-testid="order-filter-assignee" className="w-[180px] bg-white/5 border-white/10">
              <SelectValue placeholder={t("assigned_to")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("all")}</SelectItem>
              {users.filter((u) => ASSIGNABLE_ROLES.includes(u.role)).map((u) => <SelectItem key={u.id} value={u.id}>{u.name} · {t(u.role)}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filter.event_id || "all"} onValueChange={(v) => setFilter({ ...filter, event_id: v === "all" ? "" : v })}>
            <SelectTrigger data-testid="order-filter-event" className="w-[180px] bg-white/5 border-white/10">
              <SelectValue placeholder={t("event")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("all")}</SelectItem>
              {events.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
            </SelectContent>
          </Select>
          {["admin", "member"].includes(user?.role) && <CreateOrderDialog open={openCreate} setOpen={setOpenCreate} users={users} events={events} onCreated={load} />}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5">
        {STATUS_COLS.map((s) => (
          <div
            key={s}
            className={`rounded-2xl border ${STATUS_TINT[s]} p-4 min-h-[400px] transition-shadow ${overCol === s ? "ring-2 ring-indigo-400/70 shadow-lg shadow-indigo-500/20" : ""}`}
            data-testid={`kanban-col-${s}`}
            onDragOver={(e) => { e.preventDefault(); if (overCol !== s) setOverCol(s); }}
            onDragLeave={() => setOverCol((c) => (c === s ? null : c))}
            onDrop={(e) => { e.preventDefault(); dropTo(s); }}
          >
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs uppercase tracking-widest font-semibold">{t(s)}</div>
              <div className="text-xs text-slate-400">{byStatus[s].length}</div>
            </div>
            <div className="space-y-3">
              {byStatus[s].map((o) => (
                <button
                  key={o.id}
                  draggable
                  onDragStart={(e) => { setDragId(o.id); e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/plain", o.id); }}
                  onDragEnd={() => { setDragId(null); setOverCol(null); }}
                  onClick={() => setSelected(o)}
                  data-testid={`order-card-${o.id}`}
                  className={`w-full text-left glass rounded-xl p-3 hover:bg-white/10 transition-colors cursor-grab active:cursor-grabbing ${dragId === o.id ? "opacity-40" : ""}`}
                >
                  <div className="flex items-start justify-between gap-2 mb-1.5">
                    <div className="font-medium leading-tight text-sm">{o.title}</div>
                    <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-md border ${PRIORITY_TINT[o.priority]}`}>{t(o.priority)}</span>
                  </div>
                  <div className="text-[11px] text-slate-400 flex items-center gap-2">
                    <Badge variant="outline" className="border-white/10 bg-white/5 text-slate-300 text-[10px]">{t(o.type)}</Badge>
                    {o.deadline && <span className="flex items-center gap-1"><CalendarIcon className="w-3 h-3" />{o.deadline.slice(0, 10)}</span>}
                  </div>
                </button>
              ))}
              {byStatus[s].length === 0 && <div className="text-xs text-slate-500 py-6 text-center">—</div>}
            </div>
          </div>
        ))}
      </div>

      <OrderSheet order={selected} onClose={() => setSelected(null)} onChanged={load} users={users} currentUser={user} />
    </div>
  );
}

function CreateOrderDialog({ open, setOpen, users, events = [], onCreated }) {
  const { t } = useLang();
  const empty = { title: "", description: "", type: "poster", priority: "medium", deadline: "", assigned_to: "", event_id: "" };
  const [form, setForm] = useState(empty);
  const [attachments, setAttachments] = useState([]);
  const [busy, setBusy] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [tplName, setTplName] = useState("");
  const [showTpl, setShowTpl] = useState(false);

  const loadTemplates = useCallback(() => api.get("/order-templates").then((r) => setTemplates(r.data)).catch(() => {}), []);
  useEffect(() => { if (open) loadTemplates(); }, [open, loadTemplates]);
  const creativeUsers = useMemo(() => users.filter((u) => ASSIGNABLE_ROLES.includes(u.role)), [users]);
  const openEvents = useMemo(() => events.filter((e) => e.status !== "done" && e.status !== "cancelled"), [events]);

  const applyTemplate = (id) => {
    const tp = templates.find((x) => x.id === id);
    if (!tp) return;
    let deadline = "";
    if (tp.days_to_deadline != null) { const d = new Date(); d.setDate(d.getDate() + tp.days_to_deadline); deadline = d.toISOString().slice(0, 10); }
    setForm({ title: tp.title || tp.name, description: tp.description || "", type: tp.type, priority: tp.priority, deadline, assigned_to: tp.assigned_to || "", event_id: form.event_id });
    toast.success(`${t("from_template")}: ${tp.name}`);
  };

  const saveTemplate = async () => {
    if (!tplName.trim()) return;
    const days = form.deadline ? Math.max(0, Math.round((new Date(form.deadline) - new Date()) / 86400000)) : null;
    try {
      await api.post("/order-templates", { name: tplName, title: form.title, description: form.description, type: form.type, priority: form.priority, days_to_deadline: days, assigned_to: form.assigned_to || null });
      toast.success(t("save_as_template"));
      setTplName(""); setShowTpl(false); loadTemplates();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const removeTemplate = async (id) => { await api.delete(`/order-templates/${id}`); loadTemplates(); };

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/orders", { ...form, assigned_to: form.assigned_to || null, event_id: form.event_id || null, attachments });
      toast.success("OK");
      setOpen(false);
      setForm(empty);
      setAttachments([]);
      onCreated();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="order-create-button" className="btn-glow bg-indigo-500 hover:bg-indigo-600">
          <Plus className="w-4 h-4 mr-1.5" />
          {t("new_order")}
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-slate-900 border-white/10 max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{t("new_order")}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <Label className="text-xs text-slate-400">{t("from_template")}</Label>
              <Select onValueChange={applyTemplate}>
                <SelectTrigger data-testid="order-template-select" className="mt-1 bg-white/5 border-white/10"><SelectValue placeholder={templates.length ? `${templates.length} ${t("templates").toLowerCase()}` : "—"} /></SelectTrigger>
                <SelectContent>
                  {templates.map((tp) => (
                    <SelectItem key={tp.id} value={tp.id}>{tp.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button type="button" variant="outline" size="sm" data-testid="order-save-template-toggle" onClick={() => setShowTpl(!showTpl)} className="border-white/10 bg-white/5 h-10">
              <BookmarkPlus className="w-4 h-4" />
            </Button>
          </div>
          {showTpl && (
            <div className="glass rounded-lg p-3 space-y-2" data-testid="template-manager">
              <div className="flex gap-2">
                <Input data-testid="template-name-input" placeholder={t("template_name")} value={tplName} onChange={(e) => setTplName(e.target.value)} className="bg-white/5 border-white/10" />
                <Button type="button" size="sm" data-testid="template-save-btn" onClick={saveTemplate} disabled={!tplName.trim()} className="bg-indigo-500 hover:bg-indigo-600">{t("save_as_template")}</Button>
              </div>
              {templates.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {templates.map((tp) => (
                    <span key={tp.id} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md bg-white/5 border border-white/10">
                      {tp.name}
                      <button type="button" data-testid={`template-delete-${tp.id}`} onClick={() => removeTemplate(tp.id)} className="text-slate-400 hover:text-rose-300"><X className="w-3 h-3" /></button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}
          <div>
            <Label>{t("title")}</Label>
            <Input data-testid="order-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="bg-white/5 border-white/10 mt-1" />
          </div>
          <div>
            <Label>{t("description")}</Label>
            <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="bg-white/5 border-white/10 mt-1" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>{t("type")}</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger data-testid="order-type-select" className="mt-1 bg-white/5 border-white/10"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="poster">{t("poster")}</SelectItem>
                  <SelectItem value="video">{t("video")}</SelectItem>
                  <SelectItem value="photo">{t("photo")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t("priority")}</Label>
              <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                <SelectTrigger className="mt-1 bg-white/5 border-white/10"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["low","medium","high","urgent"].map(p => <SelectItem key={p} value={p}>{t(p)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>{t("deadline")}</Label>
              <Input type="date" value={form.deadline} onChange={(e) => setForm({ ...form, deadline: e.target.value })} className="bg-white/5 border-white/10 mt-1" />
            </div>
            <div>
              <Label>{t("assigned_to")}</Label>
              <Select value={form.assigned_to || "none"} onValueChange={(v) => setForm({ ...form, assigned_to: v === "none" ? "" : v })}>
                <SelectTrigger className="mt-1 bg-white/5 border-white/10"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">—</SelectItem>
                  {creativeUsers.map((u) => <SelectItem key={u.id} value={u.id}>{u.name} · {t(u.role)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>{t("event")}</Label>
            <Select value={form.event_id || "none"} onValueChange={(v) => setForm({ ...form, event_id: v === "none" ? "" : v })}>
              <SelectTrigger data-testid="order-event-select" className="mt-1 bg-white/5 border-white/10"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">{t("no_event")}</SelectItem>
                {openEvents.map((e) => <SelectItem key={e.id} value={e.id}>{e.name} · {e.start_date}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>{t("attachments")}</Label>
            <div className="mt-1.5 flex items-center gap-2">
              <UploadButton testid="order-attach-upload" label={t("upload_file")} onUpload={(f) => setAttachments([...attachments, f])} />
              <span className="text-xs text-slate-400">{attachments.length}</span>
            </div>
            <FileList items={attachments} t={t} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>{t("cancel")}</Button>
          <Button data-testid="order-create-submit" onClick={submit} disabled={busy || !form.title} className="bg-indigo-500 hover:bg-indigo-600">{busy ? "…" : t("save")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function OrderSheet({ order, onClose, onChanged, users, currentUser }) {
  const { t } = useLang();
  const [full, setFull] = useState(null);
  const [comment, setComment] = useState("");
  const [commentFiles, setCommentFiles] = useState([]);
  const [delivering, setDelivering] = useState(false);
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    if (!order) { setFull(null); setEditing(false); return; }
    api.get(`/orders/${order.id}`).then((r) => setFull(r.data));
  }, [order]);

  if (!order) return null;

  const changeStatus = async (status) => {
    await api.patch(`/orders/${order.id}`, { status });
    const { data } = await api.get(`/orders/${order.id}`);
    setFull(data);
    onChanged();
  };
  const changeAssignee = async (v) => {
    await api.patch(`/orders/${order.id}`, { assigned_to: v === "none" ? null : v });
    const { data } = await api.get(`/orders/${order.id}`);
    setFull(data);
    onChanged();
  };
  const addComment = async () => {
    if (!comment && commentFiles.length === 0) return;
    await api.post(`/orders/${order.id}/comments`, { text: comment, attachments: commentFiles });
    setComment(""); setCommentFiles([]);
    const { data } = await api.get(`/orders/${order.id}`);
    setFull(data);
  };
  const deliver = async () => {
    setDelivering(true);
    try {
      await api.post(`/orders/${order.id}/deliver`, { text: comment || "Livré", attachments: commentFiles });
      setComment(""); setCommentFiles([]);
      const { data } = await api.get(`/orders/${order.id}`);
      setFull(data);
      onChanged();
      toast.success(t("deliver"));
    } finally { setDelivering(false); }
  };
  const remove = async () => {
    await api.delete(`/orders/${order.id}`);
    onChanged(); onClose();
  };

  const o = full || order;
  const canDeliver = currentUser?.id === o.assigned_to || currentUser?.role === "admin" || currentUser?.role === "agent";
  const canEdit = ["admin", "member"].includes(currentUser?.role);

  return (
    <Sheet open={!!order} onOpenChange={(v) => !v && onClose()}>
      <SheetContent className="bg-slate-900 border-white/10 w-full sm:max-w-xl overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="pr-8 flex items-center gap-2">
            {o.title}
            {canEdit && (
              <button data-testid="order-edit-btn" onClick={() => setEditing(!editing)} className="w-7 h-7 rounded-md flex items-center justify-center hover:bg-white/10 text-slate-300" title={t("edit_order")}>
                <Pencil className="w-3.5 h-3.5" />
              </button>
            )}
          </SheetTitle>
        </SheetHeader>
        <div className="mt-4 space-y-4 text-sm">
          {editing && canEdit && (
            <EditOrderForm order={o} onSaved={async () => { setEditing(false); const { data } = await api.get(`/orders/${order.id}`); setFull(data); onChanged(); }} onCancel={() => setEditing(false)} />
          )}
          <div className="flex flex-wrap gap-2">
            <Badge variant="outline" className="border-white/10 bg-white/5">{t(o.type)}</Badge>
            <span className={`text-xs px-2 py-0.5 rounded-md border ${PRIORITY_TINT[o.priority]}`}>{t(o.priority)}</span>
            {o.deadline && <Badge variant="outline" className="border-white/10 bg-white/5"><CalendarIcon className="w-3 h-3 mr-1" />{o.deadline.slice(0, 10)}</Badge>}
          </div>
          <p className="text-slate-300 whitespace-pre-wrap">{o.description || "—"}</p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-slate-400">{t("status")}</Label>
              <Select value={o.status} onValueChange={changeStatus}>
                <SelectTrigger data-testid="order-status-select" className="mt-1 bg-white/5 border-white/10"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["pending","in_progress","review","completed","rejected"].map(s => <SelectItem key={s} value={s}>{t(s)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-400">{t("assigned_to")}</Label>
              <Select value={o.assigned_to || "none"} onValueChange={changeAssignee} disabled={!canEdit}>
                <SelectTrigger data-testid="order-assignee-select" className="mt-1 bg-white/5 border-white/10 disabled:opacity-60"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">—</SelectItem>
                  {users.filter((u) => ASSIGNABLE_ROLES.includes(u.role)).map((u) => <SelectItem key={u.id} value={u.id}>{u.name} · {t(u.role)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <WhatsAppNotify order={o} users={users} t={t} />

          {o.attachments?.length > 0 && (
            <div>
              <div className="text-xs uppercase tracking-widest text-slate-400 mb-1.5">{t("attachments")}</div>
              <FileList items={o.attachments} t={t} />
            </div>
          )}

          {o.deliverables?.length > 0 && (
            <div>
              <div className="text-xs uppercase tracking-widest text-slate-400 mb-1.5">{t("deliverables")}</div>
              <div className="space-y-2">
                {o.deliverables.map((d) => (
                  <div key={d.id} className="glass rounded-lg p-3">
                    <div className="text-xs text-slate-400 mb-1">{d.delivered_by_name} · {new Date(d.delivered_at).toLocaleString()}</div>
                    <div className="text-sm">{d.message}</div>
                    <FileList items={d.files} t={t} />
                  </div>
                ))}
              </div>
            </div>
          )}

          <div>
            <div className="text-xs uppercase tracking-widest text-slate-400 mb-2">{t("comments_activity")}</div>
            <div className="space-y-2 mb-3">
              {(o.comments || []).map((c) => (
                <div key={c.id} className="glass rounded-lg p-3">
                  <div className="text-xs text-slate-400 mb-1">{c.author_name} · {new Date(c.created_at).toLocaleString()}</div>
                  <div className="text-sm">{c.text}</div>
                  <FileList items={c.attachments} t={t} />
                </div>
              ))}
              {(!o.comments || o.comments.length === 0) && <div className="text-xs text-slate-500">—</div>}
            </div>
            <div className="space-y-2">
              <Textarea placeholder={t("quick_add_comment")} value={comment} onChange={(e) => setComment(e.target.value)} data-testid="order-comment-input" className="bg-white/5 border-white/10" />
              <div className="flex items-center gap-2 flex-wrap">
                <UploadButton testid="order-comment-upload" label={t("upload_file")} onUpload={(f) => setCommentFiles([...commentFiles, f])} />
                <span className="text-xs text-slate-400">{commentFiles.length}</span>
                <div className="ml-auto flex items-center gap-2">
                  <Button size="sm" onClick={addComment} data-testid="order-comment-send" className="bg-white/10 hover:bg-white/20">
                    <Send className="w-3.5 h-3.5 mr-1.5" />
                    {t("add_comment")}
                  </Button>
                  {canDeliver && (
                    <Button size="sm" onClick={deliver} disabled={delivering} data-testid="order-deliver-btn" className="bg-emerald-500 hover:bg-emerald-600">
                      {delivering ? "…" : t("deliver")}
                    </Button>
                  )}
                </div>
              </div>
              <FileList items={commentFiles} t={t} />
            </div>
          </div>

          {currentUser?.role === "admin" && (
            <div className="pt-4 border-t border-white/5">
              <Button variant="destructive" size="sm" onClick={remove} data-testid="order-delete-btn">
                <Trash2 className="w-3.5 h-3.5 mr-1.5" />
                {t("delete")}
              </Button>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function EditOrderForm({ order, onSaved, onCancel }) {
  const { t } = useLang();
  const [form, setForm] = useState({ title: order.title || "", description: order.description || "", deadline: (order.deadline || "").slice(0, 10), priority: order.priority || "medium", type: order.type || "poster" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/orders/${order.id}`, { ...form, deadline: form.deadline || null });
      toast.success(t("order_updated"));
      onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusy(false); }
  };
  return (
    <div className="glass rounded-xl p-4 space-y-3" data-testid="order-edit-form">
      <div className="text-xs uppercase tracking-widest text-slate-400">{t("edit_order")}</div>
      <div>
        <Label className="text-xs text-slate-400">{t("title")}</Label>
        <Input data-testid="order-edit-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1 bg-white/5 border-white/10" />
      </div>
      <div>
        <Label className="text-xs text-slate-400">{t("description")}</Label>
        <Textarea data-testid="order-edit-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1 bg-white/5 border-white/10" />
      </div>
      <div className="grid grid-cols-3 gap-2">
        <div>
          <Label className="text-xs text-slate-400">{t("deadline")}</Label>
          <Input data-testid="order-edit-deadline" type="date" value={form.deadline} onChange={(e) => setForm({ ...form, deadline: e.target.value })} className="mt-1 bg-white/5 border-white/10" />
        </div>
        <div>
          <Label className="text-xs text-slate-400">{t("priority")}</Label>
          <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
            <SelectTrigger className="mt-1 bg-white/5 border-white/10"><SelectValue /></SelectTrigger>
            <SelectContent>{["low", "medium", "high", "urgent"].map((p) => <SelectItem key={p} value={p}>{t(p)}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs text-slate-400">{t("type")}</Label>
          <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
            <SelectTrigger className="mt-1 bg-white/5 border-white/10"><SelectValue /></SelectTrigger>
            <SelectContent>{["poster", "video", "photo"].map((p) => <SelectItem key={p} value={p}>{t(p)}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      <div className="flex justify-end gap-2">
        <Button size="sm" variant="ghost" onClick={onCancel}>{t("cancel")}</Button>
        <Button size="sm" data-testid="order-edit-save" onClick={save} disabled={busy || !form.title.trim()} className="bg-indigo-500 hover:bg-indigo-600">{busy ? "…" : t("save")}</Button>
      </div>
    </div>
  );
}
